import { useEffect, useMemo, useRef, useState } from "react";

import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import {
  ArchiveBoxArrowDownIcon,
  ArchiveBoxIcon,
  ArrowUturnLeftIcon,
  ArrowUturnUpIcon,
  ArrowTrendingUpIcon,
  BanknotesIcon,
  BeakerIcon,
  BookOpenIcon,
  BriefcaseIcon,
  BuildingOfficeIcon,
  BuildingStorefrontIcon,
  CalculatorIcon,
  CalendarDaysIcon,
  CameraIcon,
  ChatBubbleLeftRightIcon,
  ClipboardDocumentListIcon,
  ClipboardDocumentCheckIcon,
  ClockIcon,
  Cog6ToothIcon,
  CurrencyPoundIcon,
  DocumentChartBarIcon,
  DocumentMagnifyingGlassIcon,
  DocumentTextIcon,
  EnvelopeIcon,
  EnvelopeOpenIcon,
  FolderPlusIcon,
  FunnelIcon,
  HandRaisedIcon,
  HomeIcon,
  InboxArrowDownIcon,
  InboxIcon,
  InboxStackIcon,
  LifebuoyIcon,
  MagnifyingGlassPlusIcon,
  NewspaperIcon,
  QuestionMarkCircleIcon,
  ReceiptRefundIcon,
  RectangleGroupIcon,
  RocketLaunchIcon,
  ShieldExclamationIcon,
  ScaleIcon,
  TagIcon,
  TruckIcon,
  WrenchScrewdriverIcon,
  XCircleIcon,
  ViewColumnsIcon,
} from "@heroicons/react/24/outline";

import Topbar from "../components/shared/Navbar/Topbar";
import LoadingWheel from "../components/shared/LoadingWheel";
import ChatPopup from "../components/chat/ChatPopup";
import { NavigationItemInterface } from "../types/Navigation";
import { usePageNameContext } from "../contexts/PageTitleContext";
import { ScannerProvider } from "../contexts/ScannerContext";
import MobileSidebar from "../components/shared/Navbar/MobileSidebar";
import DesktopSidebar from "../components/shared/Navbar/DesktopSidebar";
import {
  canAccessFeature,
  featureForRoutePath,
  FeatureScopes,
} from "../auth/featureScopes";
import { resolveLandingPath } from "../auth/landingPages";
import { useAuth } from "../contexts/AuthContext";
import useCustomerDetails from "../data/useCustomerDetails";
import usePricingAccess from "../hooks/usePricingAccess";
import { useFeatureEnabled } from "../hooks/useFeatureFlag";
import { GetFullBrandResponse } from "../models/Customer";
import { wilmaSAVCustomerId, wilmaSSOAppRegistration, wilmaSSODashboardUrl } from "../config";
import useNotificationStats from "../data/useNotificationStats";
import { INotificationStats } from "../models/notifications/Notifications";
import useSettingSet from "../data/useSettingSet";
import useSingleSetting from "../data/useSingleSetting";
import { CURRENCY_SETTING_KEY, setCurrencyCode } from "../utils/currency";
import { useChatUnreadTotal } from "../data/useChat";
import isLocalDebug from "../utils/isLocalDebug";

function withFeatureScopeGate(
  items: NavigationItemInterface[],
  featureScopes: FeatureScopes | undefined,
  isTme: boolean
): NavigationItemInterface[] {
  return items.map((item) => {
    const feature = featureForRoutePath(item.to === "/" ? "" : item.to, isTme);
    if (!feature) return item;
    if (canAccessFeature(featureScopes, feature)) return item;
    return { ...item, hidden: true };
  });
}

export default function DashboardLayout() {
  const { t } = useTranslation("navigation");
  const {
    pageInfo: { name, desc, fill, nameSuffix },
  } = usePageNameContext();
  const { loading, loggedIn, claims, featureScopes, defaultRoute, customerId } = useAuth();
  const isSavCustomer = !!customerId && customerId === wilmaSAVCustomerId;
  const { canViewPrices } = usePricingAccess();
  const location = useLocation();
  const navigate = useNavigate();
  // Set once this page load has followed the role's default route, so the sidebar's
  // Dashboard link still works afterwards for anyone allowed to see it.
  const landingRedirectDone = useRef(false);
  const { brand, features } = useCustomerDetails();
  const messagesInboxEnabled = useFeatureEnabled("Messages Inbox");
  // Status-based (not presence): a Returns flag that's been switched off keeps the old eBay Returns page.
  const returnsEnabled = useFeatureEnabled("Returns");
  const { notifications } = useNotificationStats(loading);
  const { isSet: hasRsKey } = useSettingSet("Rs:ApiKey", loading);
  // The business currency drives every PriceFormat/currencySymbol call, so it is applied before
  // any page renders (absent setting = GBP, see src/utils/currency.ts).
  const { singleSetting: currencySetting } = useSingleSetting(CURRENCY_SETTING_KEY, loading);
  const [currencyReady, setCurrencyReady] = useState(false);
  //Total of unread chat messages excluding muted channels - drives the
  //"Team Chat" sidebar badge globally so it stays visible from any page.
  const chatUnreadTotal = useChatUnreadTotal();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [featureFlags, setFeatureFlags] = useState<string[] | undefined>(undefined);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem("sidebarCollapsed") === "true";
    } catch {
      return false;
    }
  });

  const tailwindShades = [50, 100, 200, 300, 400, 500, 600, 700, 800, 900];

  // Function to create/update favicon
  const updateIcon = (rel: string, size: string | null, href: string) => {
    let selector = size ? `link[rel='${rel}'][sizes='${size}']` : `link[rel='${rel}']`;
    let link = document.querySelector(selector) as HTMLLinkElement;
    if (!link) {
      link = document.createElement("link");
      link.rel = rel;
      if (size) link.setAttribute("sizes", size);
      document.head.appendChild(link);
    }
    link.href = `${href}?v=${new Date().getTime()}`;
  };

  const handleUpdateIcons = (brand: GetFullBrandResponse) => {
    const { faviconUrl, favicon16Url, favicon32Url, appleTouchIconUrl, androidChrome192Url, androidChrome512Url } = brand;

    //favicon
    updateIcon("icon", "16x16", favicon16Url);
    updateIcon("icon", "32x32", favicon32Url);
    updateIcon("icon", null, faviconUrl);
    updateIcon("shortcut icon", null, faviconUrl);

    if (appleTouchIconUrl)
      updateIcon("apple-touch-icon", null, appleTouchIconUrl);

    if (androidChrome192Url)
      updateIcon("icon", "192x192", androidChrome192Url);

    if (androidChrome512Url)
      updateIcon("icon", "512x512", androidChrome512Url);

  }

  useEffect(() => {
    if (loading || currencySetting.isLoading) return;
    setCurrencyCode(currencySetting.data?.data?.value);
    setCurrencyReady(true);
  }, [loading, currencySetting.isLoading, currencySetting.data]);

  useEffect(() => {
    if (brand.isLoading || features.isLoading) return;

    if (brand.data?.data && tailwindShades.length > 0 && brand.data.data.primaryTailwindColours) {
      console.log("Brand data loaded:", brand.data.data);
      // Set the brand colours
      tailwindShades.forEach((shade, index) => {
        if (!brand.data.data.primaryTailwindColours[shade]) {
          console.warn(`Primary tailwind colour for shade ${shade} is not defined.`);
        } else document.documentElement.style.setProperty(`--primary-${shade}`, brand.data.data.primaryTailwindColours[shade]);
      });

      handleUpdateIcons(brand.data.data);
    }

    if (!features.data) {
      console.log("no feature data")
      return;
    }

    const flags = features.data?.data.map((feature) => feature.name);
    setFeatureFlags(flags);

  }, [brand.isLoading, features.isLoading, brand.data, features.data, notifications.data]);

  useEffect(() => {
    if (claims && !loading) {
      if (claims?.isExporter) {
        //if at / then redirect exporter to exports
        if (window.location.pathname === "/") {
          window.location.href = "/exports";
        }
      }
    }
  }, [claims, loading, window.location.pathname]);

  useEffect(() => {
    try {
      localStorage.setItem("sidebarCollapsed", isSidebarCollapsed.toString());
    } catch { }
  }, [isSidebarCollapsed]);

  if (!loading && !loggedIn) {
    window.location.assign(
      `${wilmaSSODashboardUrl}?ari=${wilmaSSOAppRegistration}&returnUrl=${window.location.href}`
    );
  }

  const staffNavigation = (featureFlags: string[] | undefined, notifications: INotificationStats | undefined, chatUnread: number): NavigationItemInterface[] => {
    if (!featureFlags) return [];
    const isCallCentre = featureFlags.includes("Call Centre");
    const isTme = featureFlags.includes("TME");
    // Hide TME-restricted pages everywhere except localhost/debug so they stay reachable while developing.
    const hideForTme = isTme && !isLocalDebug();
    const hasTriage = featureFlags.includes("Triage");
    const leadManagementEnabled = featureFlags.includes("Lead Management");
    // TME hides Leads when Lead Management is off, including on localhost.
    // A greyed-out group is not useful, and TME only want it when they have access.
    const hideLeads = hideForTme || (isTme && !leadManagementEnabled);
    // For TME customers the Vehicles group is split into Ops Management and Dismantling.
    const opsManagementGroup = isTme ? t("Ops Management") : t("Vehicles");
    const dismantlingGroup = isTme ? t("Dismantling") : t("Vehicles");

    return [
      { name: t("Dashboard"), to: "/", icon: HomeIcon },
      { name: t("Team Chat"), to: "chat", icon: ChatBubbleLeftRightIcon, notificationCount: chatUnread > 0 ? chatUnread : undefined },

      //** Leads **/
      // For TME customers the whole Leads group is hidden (rather than shown collapsed/disabled)
      { name: t("Quotes"), to: "quotes", icon: BookOpenIcon, group: t("Leads"), disabled: !leadManagementEnabled || !isCallCentre, hidden: !isCallCentre || hideLeads },
      { name: isCallCentre ? t("Bookings") : t("Leads"), to: isCallCentre ? "bookings" : "leads", icon: FolderPlusIcon, group: t("Leads"), disabled: !leadManagementEnabled, notificationCount: notifications?.leadsAwaitingContact, hidden: hideLeads },
      { name: t("Collected"), to: "vehicles-collected", icon: ArchiveBoxIcon, group: t("Leads"), disabled: !leadManagementEnabled, hidden: hideLeads },
      { name: t("Collections Board"), to: "collections-board", icon: ViewColumnsIcon, group: t("Leads"), disabled: !leadManagementEnabled, hidden: hideLeads },
      //{ name: "Calendar", to: "calendar", icon: CalendarDaysIcon },
      { name: t("Trip Manager"), to: "trip-manager", icon: CalendarDaysIcon, group: t("Leads"), disabled: !leadManagementEnabled, hidden: hideLeads },
      { name: t("Legacy Trip Manager"), to: "old-trip-manager", icon: CalendarDaysIcon, group: t("Leads"), disabled: !leadManagementEnabled, hidden: hideLeads },
      { name: t("Drop Offs"), to: "drop-offs", icon: InboxArrowDownIcon, group: t("Leads"), disabled: !leadManagementEnabled, hidden: isTme },
      { name: t("Schedule"), to: "schedule", icon: NewspaperIcon, group: t("Leads"), disabled: !leadManagementEnabled, hidden: hideLeads },

      // ** Accounts **/
      { name: t("Accounts"), to: "accounts", icon: BuildingOfficeIcon, group: t("Accounts"), disabled: false },
      { name: t("Invoices"), to: "invoices", icon: DocumentTextIcon, group: t("Accounts"), disabled: false },
      { name: t("Sales Quotes"), to: "sales-quotes", icon: ClipboardDocumentListIcon, group: t("Accounts"), disabled: false },
      { name: t("Till"), to: "till", icon: BuildingStorefrontIcon, group: t("Accounts"), hidden: !featureFlags.includes("Till") },

      //** Payments **/
      { name: t("Payments"), to: "payments", icon: BanknotesIcon, group: t("Leads"), disabled: !featureFlags.includes("Customer Payments"), hidden: hideLeads || (isTme && !featureFlags.includes("Customer Payments")) }, //Not working - notificationCount: notifications?.leadsRaisedPayments
      { name: t("Salvage"), to: "salvage", icon: TruckIcon, group: t("Leads"), hidden: hideLeads || !isSavCustomer }, 

      //** Vehicles  **/
      { name: t("Vehicles"), to: "vehicles", icon: TruckIcon, group: t("Vehicles"), disabled: false },
      { name: t("Waste Tracking"), to: "waste-tracking", icon: ClipboardDocumentCheckIcon, group: t("Vehicles"), hidden: !featureFlags.includes("DEFRA") },
      { name: t("Vehicle Board"), to: "vehicle-board", icon: ViewColumnsIcon, group: opsManagementGroup, disabled: !isTme, hidden: !isTme },
      { name: t("Drop Offs"), to: "drop-offs", icon: InboxArrowDownIcon, group: opsManagementGroup, hidden: !isTme },
      { name: t("Awaiting Inventory"), to: "parts-manager", icon: WrenchScrewdriverIcon, group: t("Vehicles"), disabled: !featureFlags.includes("Parts Tracker"), notificationCount: notifications?.vehiclesAwaitingSchedule, hidden: hideForTme },
      { name: t("Triage"), to: "triage", icon: FunnelIcon, group: dismantlingGroup, disabled: !hasTriage, hidden: !hasTriage },
      { name: t("Depollution"), to: "depollution", icon: BeakerIcon, group: dismantlingGroup, disabled: !featureFlags.includes("Parts Tracker"), hidden: hideForTme },
      { name: t("Jobs"), to: "jobs", icon: BriefcaseIcon, group: t("Vehicles"), disabled: !featureFlags.includes("Parts Tracker"), hidden: hideForTme },
      { name: t(isTme ? "Dismantling Planner" : "Production Planner"), to: "production-planner", icon: RectangleGroupIcon, group: opsManagementGroup, disabled: !featureFlags.includes("Production Manager"), hidden: !featureFlags.includes("Production Manager") },
      { name: t(isTme ? "Leader Dashboard" : "Production Dashboard"), to: "production-dashboard", icon: ViewColumnsIcon, group: opsManagementGroup, disabled: !featureFlags.includes("Production Manager"), hidden: !featureFlags.includes("Production Manager") },
      { name: t("Station Manager"), to: "station-manager", icon: WrenchScrewdriverIcon, group: dismantlingGroup, disabled: !featureFlags.includes("Production Manager"), hidden: !featureFlags.includes("Production Manager") },

      //** Parts **/
      { name: t("Work Orders"), to: "work-orders", icon: ClipboardDocumentListIcon, group: t("Parts"), disabled: !featureFlags.includes("Parts Tracker") },
      { name: t("Exports"), to: "exports", icon: RocketLaunchIcon, group: t("Parts"), disabled: !featureFlags.includes("Parts Tracker"), hidden: hideForTme },
      { name: t("Awaiting Arrival"), to: "awaiting-arrival", icon: ClockIcon, group: t("Parts"), disabled: !leadManagementEnabled, hidden: hideForTme },
      { name: t("Parts Checker"), to: "parts-checker", icon: ClipboardDocumentCheckIcon, group: t("Parts"), disabled: !featureFlags.includes("Parts Tracker"), hidden: hideForTme },
      { name: t("Stock"), to: "stock", icon: InboxStackIcon, group: t("Parts"), disabled: !featureFlags.includes("Parts Tracker") },
      { name: t("RS Core"), to: "core", icon: InboxIcon, group: t("Parts"), disabled: !hasRsKey },
      // Bins don't depend on the RS integration, so this is gated on Parts Tracker like Stock
      // rather than on hasRsKey - it just lives next to RS Core in the list.
      { name: t("Bins"), to: "core-bins", icon: ArchiveBoxIcon, group: t("Parts"), disabled: !featureFlags.includes("Parts Tracker") },
      { name: t("Quality Control"), to: "stock-qc", icon: ShieldExclamationIcon, group: t("Parts"), disabled: !featureFlags.includes("Parts Tracker") },
      { name: t("Part Requests"), to: "part-requests", icon: HandRaisedIcon, group: t("Parts"), disabled: !featureFlags.includes("Part Requests"), notificationCount: notifications?.partRequestsOpen },
      { name: t("Bids"), to: "bids", icon: ScaleIcon, group: t("Parts"), disabled: !featureFlags.includes("PartPro Trade"), hidden: !featureFlags.includes("PartPro Trade") },

      //** eBay **/
      { name: t("Unlisted Parts"), to: "ebay-unlisted", icon: DocumentTextIcon, group: t("Order Management"), disabled: !featureFlags.includes("eBay Management"), notificationCount: notifications?.partsUnlisted, hidden: hideForTme },
      { name: t("Recently Listed"), to: "recently-listed", icon: ReceiptRefundIcon, group: t("Order Management"), disabled: !featureFlags.includes("eBay Management"), hidden: hideForTme },
      { name: t("Active Listings"), to: "ebay-listings", icon: CurrencyPoundIcon, group: t("Order Management"), disabled: !featureFlags.includes("eBay Management"), hidden: hideForTme },
      { name: t("Listing Optimisation"), to: "ebay-optimisation", icon: ArrowTrendingUpIcon, group: t("Order Management"), disabled: !featureFlags.includes("eBay Optimisation"), hidden: !featureFlags.includes("eBay Optimisation") || hideForTme },
      { name: t("Pricing"), to: "pricing", icon: BanknotesIcon, group: t("Order Management"), disabled: !featureFlags.includes("eBay Optimisation"), hidden: !featureFlags.includes("eBay Optimisation") || hideForTme || !canViewPrices },
      { name: t("Orders"), to: "ebay-orders", icon: ArchiveBoxArrowDownIcon, group: t("Order Management"), disabled: !featureFlags.includes("eBay Management"), notificationCount: notifications?.partsNewOrders },
      // The unified inbox: live behind the "Messages Inbox" flag, sample data on localhost.
      { name: t("Messages"), to: "messages", icon: EnvelopeIcon, group: t("Order Management"), hidden: !messagesInboxEnabled && !isLocalDebug() },
      // Returns (eBay and PartPro) behind the "Returns" flag; it replaces the old eBay Returns page when on.
      { name: t("Returns"), to: "returns", icon: ArrowUturnLeftIcon, group: t("Order Management"), hidden: !returnsEnabled && !isLocalDebug(), notificationCount: notifications?.returnsWaitingResponse },
      { name: t("eBay Returns"), to: "ebay-returns", icon: ArrowUturnUpIcon, group: t("Order Management"), disabled: !featureFlags.includes("eBay Management"), hidden: hideForTme || returnsEnabled },

      //** Widget **/
      // TME never use the widget, so the group is hidden even on localhost.
      { name: t("Dashboard"), to: "widget-dashboard", icon: HomeIcon, group: t("Widget"), hidden: isTme },
      { name: t("Calculator"), to: "widget-calculator", icon: CalculatorIcon, group: t("Widget"), hidden: isTme },
      { name: t("Postcodes"), to: "widget-postcodes", icon: DocumentMagnifyingGlassIcon, group: t("Widget"), hidden: isTme },
      { name: t("Suppliers"), to: "widget-suppliers", icon: BuildingStorefrontIcon, group: t("Widget"), hidden: isTme },
      { name: t("SellAVehicle"), to: "sellavehicle", icon: TruckIcon, group: t("Widget"), hidden: isTme },
      { name: t("History"), to: "abandoned-quotes", icon: BookOpenIcon, group: t("Widget"), hidden: isTme },
      { name: t("Configuration"), to: "widget-config", icon: Cog6ToothIcon, group: t("Widget"), hidden: isTme },
      // { name: "Contacts", to: "contacts", icon: UsersIcon },

      //Reporting - moved out of the Admin group so a non-admin role can be given it. These routes
      //map to feature "reporting" in ROUTE_FEATURE_MAP, so withFeatureScopeGate hides them from
      //any role without the scope - including every role that predates it.
      { name: t("Reports"), to: "reports", icon: DocumentChartBarIcon, group: t("Reporting") },
      { name: t("Performance Tracking"), to: "performance", icon: ArrowTrendingUpIcon, group: t("Reporting") },
      { name: t("Stock Check Report"), to: "stock-check-report", icon: ClipboardDocumentCheckIcon, group: t("Reporting") },
    ];
  };

  const driverNavigation: NavigationItemInterface[] = [
    { name: t("Find Lead"), to: "find-lead", icon: DocumentMagnifyingGlassIcon },
  ];

  const exporterNavigation: NavigationItemInterface[] = [
    { name: t("Exports"), to: "exports", icon: RocketLaunchIcon },
  ];

  const adminNavigation = (featureFlags: string[] | undefined, notifications: INotificationStats | undefined): NavigationItemInterface[] => {
    if (!featureFlags) return [];

    const hideForTme = featureFlags.includes("TME") && !isLocalDebug();
    const isTme = featureFlags.includes("TME");

    const navigation: NavigationItemInterface[] = [
      //** Admin **/
      { name: t("eBay Messages"), to: "ebay-messages", icon: EnvelopeIcon, group: t("Admin"), notificationCount: notifications?.ebayUnreadMessages },
      { name: t("PartPro Messages"), to: "partpro-messages", icon: EnvelopeOpenIcon, group: t("Admin"), notificationCount: notifications?.partProUnreadMessages },
      { name: t("Find Lead"), to: "find-lead", icon: DocumentMagnifyingGlassIcon, group: t("Admin"), hidden: hideForTme },
      { name: t("Part Categorisation"), to: "parts-editor", icon: RectangleGroupIcon, group: t("Admin") },
      { name: t("Category Editor"), to: "category-editor", icon: TagIcon, group: t("Admin") },
      // TME only: the central complexity list is matched against on lead creation for TME customers
      // and nobody else, so for anyone else the page would be a rule list that grades nothing.
      { name: t("Vehicle Complexity"), to: "vehicle-complexity", icon: ScaleIcon, group: t("Admin"), hidden: !isTme },
      { name: t("Required Images"), to: "category-images", icon: CameraIcon, group: t("Admin") },
      { name: t("Support Tickets"), to: "tickets", icon: LifebuoyIcon, group: t("Admin") },
      { name: t("Driver Tracking"), to: "driver-tracking", icon: MagnifyingGlassPlusIcon, group: t("Admin"), disabled: !featureFlags.includes("Driver Tracking") },
      { name: t("Shopify Backfill"), to: "shopify-backfill", icon: BuildingStorefrontIcon, group: t("Admin") },
      // Admin-only for now, so it lives in the admin list - grouped with the other reports.
      { name: t("Mechanic Damage Report"), to: "mechanic-damage", icon: ArrowTrendingUpIcon, group: t("Reporting") },
      { name: t("Admin Settings"), to: "admin", icon: Cog6ToothIcon, group: t("Admin") },
    ];

    if (isLocalDebug()) {
      //One entry for all the Shopify tools - they live behind a hub page rather than four siblings.
      navigation.push({ name: "Shopify", to: "debug/shopify", icon: BuildingStorefrontIcon, group: "Debug" });
      navigation.push({ name: "eBay → PartPro Backfill", to: "debug/partpro-ebay-backfill", icon: WrenchScrewdriverIcon, group: "Debug" });
      navigation.push({ name: "Donor VIN Sweep", to: "debug/donor-vin-sweep", icon: WrenchScrewdriverIcon, group: "Debug" });
      navigation.push({ name: "Listing Refresh", to: "debug/listing-refresh", icon: WrenchScrewdriverIcon, group: "Debug" });
      navigation.push({ name: "Historical ELV Import", to: "debug/historical-import", icon: WrenchScrewdriverIcon, group: "Debug" });
    }

    return navigation;
  };

  //Get the url and check if we're at a disabled page and redirect to home
  const allNavigation: NavigationItemInterface[] = [
    ...staffNavigation(featureFlags, notifications.data?.data, chatUnreadTotal),
    ...driverNavigation,
    ...adminNavigation(featureFlags, notifications.data?.data),
  ];

  const url = window.location.pathname.replace("/", "");
  const splits = url.split("/");

  // A route can appear under multiple nav entries (e.g. "drop-offs" lives in both
  // the Leads and Vehicles groups, and only one is enabled for a given customer).
  // Only redirect home if every matching entry is disabled — otherwise the user
  // reached the page via a valid, enabled link.
  const matchingPages = allNavigation.filter((nav) => splits.includes(nav.to));
  if (matchingPages.length > 0 && matchingPages.every((nav) => nav.disabled)) {
    window.location.href = "/";
  }


  const getNavigation = (
    claims: any,
    featureFlags: string[] | undefined,
    scopes: FeatureScopes | undefined
  ): NavigationItemInterface[] => {
    if (!claims || !featureFlags) return [];

    let navigation: NavigationItemInterface[] = [];

    if (claims.isAdmin) {
      navigation.push(
        ...staffNavigation(featureFlags, notifications.data?.data, chatUnreadTotal),
        ...adminNavigation(featureFlags, notifications.data?.data)
      );
    } else if (claims.isDriver) {
      navigation.push(...driverNavigation);
    } else if (claims.isExporter) {
      navigation.push(...exporterNavigation);
    } else {
      navigation.push(...staffNavigation(featureFlags, notifications.data?.data, chatUnreadTotal));
    }

    // Ungrouped so it sits at the bottom of the sidebar for every role.
    navigation.push({ name: t("Help"), to: "help", icon: QuestionMarkCircleIcon });

    return withFeatureScopeGate(navigation, scopes, featureFlags.includes("TME"));
  };

  const navigation = useMemo(
    () => getNavigation(claims, featureFlags, featureScopes),
    [claims, featureFlags, featureScopes, hasRsKey, chatUnreadTotal, notifications.data?.data]
  );

  // Landing page: at "/" send the user to their role's default page, or - if the role
  // can't see the Dashboard at all - to the first page it can, instead of "No access".
  const landingRedirect = useMemo(() => {
    if (loading || !loggedIn || !featureFlags || location.pathname !== "/") return null;
    return resolveLandingPath({
      defaultRoute,
      navigation,
      canAccessDashboard: canAccessFeature(featureScopes, "dashboard"),
      alreadyRedirected: landingRedirectDone.current,
    });
  }, [loading, loggedIn, featureFlags, location.pathname, defaultRoute, navigation, featureScopes]);

  useEffect(() => {
    if (!landingRedirect) return;
    landingRedirectDone.current = true;
    navigate(landingRedirect, { replace: true });
  }, [landingRedirect, navigate]);

  if (loading || !featureFlags || !currencyReady) {
    return (
      <div className="h-screen w-screen flex items-center justify-center">
        <LoadingWheel />
      </div>
    );
  }

  return (
    <ScannerProvider>
      <Topbar collapsed={isSidebarCollapsed} onToggleCollapsed={() => setIsSidebarCollapsed((prev) => !prev)} setSidebarOpen={setSidebarOpen} />

      <div>
        <MobileSidebar
          sidebarOpen={sidebarOpen}
          setSidebarOpen={setSidebarOpen}
          navigation={navigation}
        />
        <DesktopSidebar
          navigation={navigation}
          collapsed={isSidebarCollapsed}

        />
        <main className={`flex flex-1 flex-col transition-all ${isSidebarCollapsed ? "md:pl-16" : "md:pl-60"}`} style={{ position: "relative", zIndex: 0, overflow: "visible" }}>
          <div className={`${!fill && "pb-6 pt-2"}`}>
            {!fill && (
              <div className="mt-2 px-4 sm:px-6 md:pl-4 prose">
                <h1 className="font-semibold text-gray-900 mb-0">
                  {name}
                  {nameSuffix && (
                    <span className="ml-3 text-xl font-semibold text-primary-600 align-middle">
                      {nameSuffix}
                    </span>
                  )}
                </h1>
                <h4 className="text-base font-medium text-gray-500 mt-1">
                  {desc}
                </h4>
              </div>
            )}
            <div className={`${!fill && "px-4 sm:px-6 md:px-4"}`}>
              {/* Don't flash "No access" for the Dashboard while the redirect is in flight. */}
              {landingRedirect ? null : <Outlet />}
            </div>
          </div>
        </main>
      </div>
      <ChatPopup />
    </ScannerProvider>
  );
}
